import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '@app/env-service';
import { ApiService } from 'v2/modules/shared/services/api/api.service';
import { EnvironmentUtilService } from 'v2/modules/shared/services/util/environment-util.service';

@Injectable({
  providedIn: 'root',
})
export class ProductTagService {
  url :string = environment.url;
  gettingRecords :boolean;

  private readonly _loading = new BehaviorSubject<boolean>(true);
  public loading$ = this._loading.asObservable();

  private readonly _record = new BehaviorSubject<any>(<any>[]);
  public record$ = this._record.asObservable();

  private readonly _formattedRecord = new BehaviorSubject<any>(<any>[]);
  public formattedRecord$ = this._formattedRecord.asObservable();

  private readonly _parentRecord = new BehaviorSubject<any>(<any>[]);
  public parentRecord$ = this._parentRecord.asObservable();
  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private http :HttpClient,
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {
    this.get({});
    this.getParentTags();
  }

  get(params :any) {
    return this.http
      .get(`${this.url}/product-tags`, { params: params })
      .subscribe((resp :any) => {
        this._loading.next(false);
        this._record.next(resp);
      });
  }

  getV2(
    params :{ [param :string] :string | string[] },
    cache :boolean = false,
  ) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/product-tags`, {
      params: params,
      cache,
    });
  }

  getParentTags() {
    return this.http
      .get(`${this.url}/product-tags`, { params: { parent_only: true } })
      .subscribe((resp :any) => {
        this._loading.next(false);
        this._parentRecord.next(resp);
      });
  }

  create(data :any) :Observable<any> {
    return this.http.post(`${this.url}/product-tags`, data).pipe(
      tap((resp :any) => {
        if (resp && resp.id) {
          const records :any = this._record.getValue();
          records.splice(0, 0, resp);
          this._record.next(records);
        }
      }),
    );
  }

  update(tagId :number, data :any) :Observable<any> {
    return this.http.post(`${this.url}/product-tags/${tagId}`, data).pipe(
      tap((resp :any) => {
        if (resp && resp.id) {
          const records :any[] = this._record.getValue();
          const idx = records.findIndex((row :any) => row.id == resp.id);
          records.splice(idx, 1, resp);
          this._record.next(records);
        }
      }),
    );
  }

  delete(categoryId :number) {
    return this.http.delete(`${this.url}/product-tags/${categoryId}`).pipe(
      tap((resp :any) => {
        const records :any = this._record.getValue();
        const idx = records.findIndex((row :any) => row.id == categoryId);
        records.splice(idx, 1);
        this._record.next(records);
      }),
    );
  }

  getTags(params :any, tag :any) {
    return this.http
      .get(`${this.url}/product-tags`, { params: params })
      .subscribe((resp :any) => {
        const newList = [];
        function isDisabled(id :number) {
          if (!tag) return false;

          return tag.id === id ? true : false;
        }

        // loop until no children
        function traverse(items, level, disabled = false) {
          level += 1;

          const whitespacePrefix = '&nbsp;&nbsp;'.repeat(level);

          items.children.forEach((element) => {
            newList.push({
              level,
              ...element,
              name: whitespacePrefix + element.name,
              disabled,
              rawName: element.name,
            });
            if (element.children.length > 0)
              traverse(
                element,
                level,
                isDisabled(element.id) || disabled ? true : false,
              );
          });
        }
        resp.forEach((element) => {
          const level = 0;
          newList.push({
            level,
            ...element,
            disabled: isDisabled(element.id),
            rawName: element.name,
          });
          if (element.children.length > 0)
            traverse(element, level, isDisabled(element.id));
        });

        this._loading.next(false);
        this._formattedRecord.next(newList);
      });
  }

  formatHList(newList, tag, level, disabled = false) {
    const whitespacePrefix = '&nbsp;&nbsp;'.repeat(level);
    level += 1;
    tag.children.forEach((element) => {
      newList.push({
        level,
        ...element,
        name: whitespacePrefix + element.name,
        disabled,
        rawName: element.name,
      });
      if (element.children.length > 0) {
        return this.formatHList(newList, element, level, false);
      }
    });

    return newList;
  }

  pushTags(tag, list) {
    list = list.concat(tag);

    return tag.assigned_parent ? this.pushTags(tag.assigned_parent, list) : list;
  }

  getUniqueByKey(rowList, key) {
    const newList = [];
    rowList.forEach((row) => {
      const exist = newList.find((el) => el[key] == row[key]);
      if (!exist) {
        newList.push(row);
      }
    });

    return newList;
  }

  formatTagHierarchyList(tags :any) {
    const tagIdx = tags.findIndex((row) => row.parent_id > 0);
    if (tagIdx >= 0) {
      const tag = tags[tagIdx];
      const parent = tags.find((row) => row.id == tag.parent_id);
      if (parent && parent.id) {
        if (!parent.children) {
          parent.children = [];
        }
        parent.children.push(tag);
      }
      tags.splice(tagIdx, 1);

      return this.formatTagHierarchyList(tags);
    }

    return tags;
  }
}
